/**
 * Modules Service - API calls for modules and groups management
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://apilinkoo.creago.be';

/**
 * Get all active modules
 * @param {string} lang - Language code (fr, en, etc.)
 * @returns {Promise<Array>} List of modules
 */
export const getModules = async (lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch modules');
        return await response.json();
    } catch (error) {
        console.error('Error fetching modules:', error);
        return [];
    }
};

/**
 * Get user's modules organization with groups
 * @param {number} peopleId - User ID
 * @param {string} lang - Language code
 * @returns {Promise<Array>} Groups with their modules
 */
export const getPeopleModules = async (peopleId, lang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/people/${peopleId}?lang=${lang}`);
        if (!response.ok) throw new Error('Failed to fetch people modules');
        return await response.json();
    } catch (error) {
        console.error('Error fetching people modules:', error);
        return [];
    }
};

/**
 * Get user's module groups
 * @param {number} peopleId - User ID
 * @returns {Promise<Array>} List of groups
 */
export const getPeopleGroups = async (peopleId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/people/${peopleId}/groups`);
        if (!response.ok) throw new Error('Failed to fetch groups');
        return await response.json();
    } catch (error) {
        console.error('Error fetching groups:', error);
        return [];
    }
};

/**
 * Create a new module group
 * @param {number} peopleId - User ID
 * @param {string} name - Group name
 * @param {string} color - Optional color
 * @returns {Promise<Object>} Result with success status and groupId
 */
export const createModuleGroup = async (peopleId, name, color = '') => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/groups`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ peopleId, name, color })
        });
        return await response.json();
    } catch (error) {
        console.error('Error creating group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Update a module group
 * @param {number} groupId - Group ID
 * @param {Object} data - Update data (name, sortOrder, isCollapsed, color)
 * @returns {Promise<Object>} Result with success status
 */
export const updateModuleGroup = async (groupId, data) => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/groups/${groupId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        return await response.json();
    } catch (error) {
        console.error('Error updating group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a module group
 * @param {number} groupId - Group ID
 * @returns {Promise<Object>} Result with success status
 */
export const deleteModuleGroup = async (groupId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/groups/${groupId}`, {
            method: 'DELETE'
        });
        return await response.json();
    } catch (error) {
        console.error('Error deleting group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Add a module to a group
 * @param {number} groupId - Group ID
 * @param {number} moduleId - Module ID
 * @returns {Promise<Object>} Result with success status and itemId
 */
export const addModuleToGroup = async (groupId, moduleId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/groups/${groupId}/modules`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ moduleId })
        });
        return await response.json();
    } catch (error) {
        console.error('Error adding module to group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Remove a module from a group
 * @param {number} groupId - Group ID
 * @param {number} moduleId - Module ID
 * @returns {Promise<Object>} Result with success status
 */
export const removeModuleFromGroup = async (groupId, moduleId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/groups/${groupId}/modules/${moduleId}`, {
            method: 'DELETE'
        });
        return await response.json();
    } catch (error) {
        console.error('Error removing module from group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Reorder modules in a group
 * @param {number} groupId - Group ID
 * @param {Array<number>} moduleIds - Array of module IDs in new order
 * @returns {Promise<Object>} Result with success status
 */
export const reorderGroupModules = async (groupId, moduleIds) => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/groups/${groupId}/reorder`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ moduleIds })
        });
        return await response.json();
    } catch (error) {
        console.error('Error reordering modules:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Toggle module pinned status in a group
 * @param {number} groupId - Group ID
 * @param {number} moduleId - Module ID
 * @param {boolean} isPinned - New pinned status
 * @returns {Promise<Object>} Result with success status
 */
export const toggleModulePinned = async (groupId, moduleId, isPinned) => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/groups/${groupId}/modules/${moduleId}/pinned`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isPinned })
        });
        return await response.json();
    } catch (error) {
        console.error('Error toggling module pinned status:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get modules in a specific group
 * @param {number} groupId - Group ID
 * @returns {Promise<Array>} List of modules in the group
 */
export const getGroupModules = async (groupId) => {
    try {
        const response = await fetch(`${API_BASE}/rest/linkoo/modules/groups/${groupId}/modules`);
        if (!response.ok) throw new Error('Failed to fetch group modules');
        return await response.json();
    } catch (error) {
        console.error('Error fetching group modules:', error);
        return [];
    }
};

// Module code to navigation ID mapping
export const MODULE_NAV_MAP = {
    'CONTACTS': 'contacts',
    'CONVERSATIONS': 'messages',
    'MAP': 'map',
    'AGENDA': 'calendar',
    'EVENTS': 'events'
};

// Navigation ID to module code mapping
export const NAV_TO_MODULE_MAP = {
    'contacts': 'CONTACTS',
    'messages': 'CONVERSATIONS',
    'map': 'MAP',
    'calendar': 'AGENDA',
    'events': 'EVENTS'
};
